import React, { useState } from "react";
import {
    ArrowLeft,
    Upload,
    FileText,
    X,
    Loader2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AdminEPKServices } from "../../services/admin/AdminEPKServices";
import { useNotification } from "../../context/NotificationContext";

export default function AddEpkPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        validateAndSetFile(selectedFile);
    };

    const validateAndSetFile = (selectedFile) => {
        if (!selectedFile) return;

        const allowedExtensions = ['.pdf', '.doc', '.docx'];
        const fileName = selectedFile.name.toLowerCase();
        const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

        if (!isValidExtension) {
            showNotification({ message: "Invalid file type. Please upload a PDF or Word document.", type: "error" });
            return;
        }

        if (selectedFile.size > 500 * 1024 * 1024) {
            showNotification({ message: "File is too large. Maximum size is 500MB.", type: "error" });
            return;
        }

        setFile(selectedFile);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const selectedFile = e.dataTransfer.files[0];
        validateAndSetFile(selectedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            showNotification({ message: "Please select a file to upload.", type: "error" });
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await AdminEPKServices.uploadEPK(formData);
            if (response.status === "success") {
                showNotification({ message: "EPK uploaded successfully", type: "success" });
                navigate("/admin/epk");
            } else {
                showNotification({ message: response.message || "Failed to upload EPK", type: "error" });
            }
        } catch (error) {
            showNotification({ message: "An error occurred during upload", type: "error" });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="p-6 lg:p-10 max-w-4xl mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/admin/epk")}
                    className="flex items-center gap-2 text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white transition-all mb-8 group"
                >
                    <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold tracking-widest uppercase">Back to Management</span>
                </button>

                <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-black/5 dark:border-white/5 shadow-xl overflow-hidden">
                    <div className="p-8 lg:p-12">
                        <div className="mb-10 text-center">
                            <h2 className="text-3xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                                Upload EPK Document
                            </h2>
                            <p className="text-black/50 dark:text-white/40 text-sm">
                                Select a PDF or Word document (max 500MB).
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8">
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={`relative border-2 border-dashed rounded-3xl p-12 transition-all flex flex-col items-center justify-center gap-4 ${isDragging
                                        ? "border-jazz-brown bg-jazz-brown/5"
                                        : "border-black/10 dark:border-white/10 hover:border-jazz-brown/50"
                                    }`}
                            >
                                {!file ? (
                                    <>
                                        <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mb-2">
                                            <Upload className={isDragging ? "text-jazz-brown" : "text-black/20 dark:text-white/20"} size={32} />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-medium text-black dark:text-white mb-1">
                                                Click to upload or drag and drop
                                            </p>
                                            <p className="text-xs text-black/40 dark:text-white/30">
                                                PDF, DOC, or DOCX (max 500MB)
                                            </p>
                                        </div>
                                        <input
                                            type="file"
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx"
                                        />
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center gap-4 w-full">
                                        <div className="w-20 h-20 bg-jazz-brown/10 rounded-2xl flex items-center justify-center relative">
                                            <FileText size={40} className="text-jazz-brown" />
                                            <button
                                                type="button"
                                                onClick={() => setFile(null)}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-all shadow-md"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <div className="text-center max-w-xs">
                                            <p className="text-sm font-bold text-black dark:text-white truncate">
                                                {file.name}
                                            </p>
                                            <p className="text-xs text-black/40 dark:text-white/30">
                                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={!file || isUploading}
                                className={`w-full py-5 rounded-2xl text-sm font-bold tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${!file || isUploading
                                        ? "bg-gray-100 dark:bg-zinc-800 text-black/20 dark:text-white/20 cursor-not-allowed"
                                        : "bg-black text-white hover:bg-jazz-brown shadow-xl shadow-black/10 active:scale-[0.98]"
                                    }`}
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin" />
                                        <span>UPLOADING DOCUMENT...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={20} />
                                        <span>UPLOAD EPK NOW</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
            </div>
        </div>
    );
}
